import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Chart from "react-apexcharts";
import { Card, Modal } from "react-bootstrap";
import { ApexOptions } from "apexcharts";
import { addDays, format, startOfWeek } from "date-fns";
import HyperDatepicker from "components/Datepicker";

interface IRespondente {
  usuario_id: number;
  nome: string;
  nota: number;
}

interface IData {
  correlacao_atendimento: number;
  correlacao_casos: number;
  media_empresa: number;
  respondentes: IRespondente[];
}

function DesvioPadrao(amostra: number[]): number {
  if (amostra.length < 2) {
    return 0;
  }

  const media =
    amostra.reduce((soma, valor) => soma + valor, 0) / amostra.length;

  const somaQuadrados = amostra.reduce(
    (soma, valor) => soma + Math.pow(valor - media, 2),
    0,
  );

  const variancia = somaQuadrados / (amostra.length - 1);

  return Math.sqrt(variancia);
}
const RespondentesChart = () => {
  const [data, setData] = useState<IData>();
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [modalDetalhe, setModalDetalhe] = useState(false);
  const inicio = useMemo(
    () =>
      startOfWeek(dataSelecionada, {
        weekStartsOn: 5,
      }),
    [dataSelecionada],
  );

  const fim = useMemo(() => addDays(inicio, 6), [inicio]);

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await axios.get("/autoavaliacao-resposta/respondentes", {
        params: {
          data_inicio: format(inicio, "yyyy-MM-dd"),
          data_fim: format(fim, "yyyy-MM-dd"),
        },
      });

      console.log(data);

      setData(data);
    };

    fetchData();
  }, [dataSelecionada]);

  const categorias = useMemo(
    () => data?.respondentes.map((r) => r.nome) ?? [],
    [data],
  );

  const valores = useMemo(
    () => data?.respondentes.map((r) => r.nota) ?? [],
    [data],
  );

  const cores = useMemo(
    () =>
      valores.map((nota) => {
        if (nota < 6) return "#dc3545"; // vermelho
        if (nota < 8) return "#ffc107"; // amarelo
        return "#28a745"; // verde
      }),
    [valores],
  );

  const series = [
    {
      name: "Nota",
      data: valores,
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 500,
      toolbar: {
        show: false,
      },
    },

    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        borderRadius: 4,
      },
    },

    colors: cores,

    legend: {
      show: false,
    },

    dataLabels: {
      enabled: true,
      formatter: (val) => Number(val)?.toFixed(1),
    },

    xaxis: {
      categories: categorias,
      min: 0,
      max: 10,
      title: {
        text: "Nota (0-10)",
      },
    },

    annotations: data
      ? {
          xaxis: [
            {
              x: data.media_empresa,
              borderColor: "#4a81d4",
              strokeDashArray: 5,
              label: {
                text: `Média: ${data?.media_empresa?.toFixed(2)}`,
                style: {
                  background: "#1e1e1e",
                  color: "#fff",
                },
              },
            },
          ],
        }
      : undefined,

    tooltip: {
      y: {
        formatter: (val) => `${val?.toFixed(2)} / 10`,
      },
    },
  };
  const CoeficienteVariacao = useMemo(() => {
    return (
      (DesvioPadrao(data?.respondentes?.map((r) => r.nota) ?? []) / 10) * 100
    );
  }, [data]);
  return (
    <>
      <Card>
        <Card.Body>
          <h4 className="header-title mb-3">Autoavaliação</h4>
          <div
            style={{
              maxWidth: "8vw",
            }}
          >
            <HyperDatepicker
              hideAddon
              value={inicio}
              onChange={(date) => date && setDataSelecionada(date)}
              dateFormat="dd/MM/yyyy"
            />
          </div>

          <Chart
            options={options}
            series={series}
            type="bar"
            height={Math.max(400, categorias.length * 45)}
          />

          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "center",
              gap: 24,
              fontSize: 14,
            }}
          >
            <div>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: "#dc3545",
                  marginRight: 6,
                }}
              />
              Abaixo de 6
            </div>

            <div>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: "#ffc107",
                  marginRight: 6,
                }}
              />
              Entre 6 e 8
            </div>

            <div>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: "#28a745",
                  marginRight: 6,
                }}
              />
              Acima de 8
            </div>
          </div>
          <div
            style={{
              marginTop: "2%",
              width: "clamp(1px, 70%, 850px)",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "inline-block",
                fontWeight: "normal",
                height: "clamp(1px, 30%, 50px)",
                fontSize: "clamp(1px, 0.9vw, 14px)",
                padding: "0.35em 0.65em",
                borderRadius: "50rem",
                margin: "1%",
                color: "#fff",
                backgroundColor:
                  CoeficienteVariacao > 30 ? "#dc3545" : "#198754",
              }}
            >
              Coeficiente de Variação = {CoeficienteVariacao?.toFixed(0)}%
            </div>

            <div
              style={{
                display: "inline-block",
                fontWeight: "normal",
                fontSize: "clamp(1px, 0.9vw, 14px)",
                margin: "1%",
                height: "clamp(1px, 30%, 50px)",
                padding: "0.35em 0.65em",
                borderRadius: "50rem",
                color: "#fff",
                backgroundColor:
                  Math.abs(data?.correlacao_atendimento) > 0.5
                    ? "#dc3545"
                    : "#198754",
              }}
            >
              Relação com horas = {data?.correlacao_atendimento?.toFixed(2)}
            </div>

            <div
              style={{
                display: "inline-block",
                fontWeight: "normal",
                fontSize: "clamp(1px, 0.9vw, 14px)",
                padding: "0.35em 0.65em",
                height: "clamp(1px, 30%, 50px)",
                margin: "1%",
                borderRadius: "50rem",
                color: "#fff",
                backgroundColor:
                  Math.abs(data?.correlacao_casos) > 0.5
                    ? "#dc3545"
                    : "#198754",
              }}
            >
              Relação com nº de casos = {data?.correlacao_casos?.toFixed(2)}
            </div>
            <i
              onClick={() => {
                setModalDetalhe(true);
              }}
              style={{
                fontSize: "clamp(1px, 2vw, 30px)",
                height: "clamp(1px, 30%, 50px)",
                alignSelf: "center",
                cursor: "pointer",
              }}
              className="mdi mdi-help-circle-outline"
            />
          </div>
        </Card.Body>
      </Card>
      <Modal
        size="lg"
        show={modalDetalhe}
        onHide={() => {
          setModalDetalhe(false);
        }}
      >
        <Modal.Header>
          <Modal.Title>Detalhes de Métricas de autoavaliação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card>
            <Card.Header>
              <Card.Title>Coeficiente de Variação</Card.Title>
            </Card.Header>
            <Card.Body>
              <Card.Text>
                <span style={{ fontWeight: "bold" }}>
                  Medida de variação das notas de autoavaliação.
                </span>
              </Card.Text>
              <Card.Text>
                Porcentagem maior que 30% indica variação mais alta que o
                normal.
              </Card.Text>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>
              <Card.Title>Relação com horas</Card.Title>
            </Card.Header>
            <Card.Body>
              <Card.Text>
                <span style={{ fontWeight: "bold" }}>
                  Teste de Spearman entre a nota da autoavaliação e a quantidade
                  de horas de atendimento do colaborador.
                </span>
              </Card.Text>
              <Card.Text>
                Valores próximos de 1: quanto mais horas, melhor a nota.
              </Card.Text>
              <Card.Text>
                Valores próximos de -1: quanto mais horas, pior a nota,
              </Card.Text>
              <Card.Text>
                Valores próximos de 0: ausência de correlação
              </Card.Text>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>
              <Card.Title>Relação com casos</Card.Title>
            </Card.Header>
            <Card.Body>
              <Card.Text>
                <span style={{ fontWeight: "bold" }}>
                  Teste de Spearman entre a nota da autoavaliação e a quantidade
                  de casos de pacientes do colaborador.
                </span>
              </Card.Text>
              <Card.Text>
                Valores próximos de 1: quanto mais casos, melhor a nota.
              </Card.Text>
              <Card.Text>
                Valores próximos de -1: quanto mais casos, pior a nota,
              </Card.Text>
              <Card.Text>
                Valores próximos de 0: ausência de correlação
              </Card.Text>
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default RespondentesChart;
